import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import { DividendsEndpoints } from './dividendsEndpoints';
import {
    gelNumberOf,
    mapCalculateDistribution,
    mapCommitDeclaration,
    mapCreateShareholder,
    mapDeclarationDetail,
    mapDeclarationList,
    mapPreviewDeclaration,
    mapShareholderList,
    mapUpdateShareholder,
} from './dividendsMapping';
import type { IDividendsService } from './IDividendsService';
import type {
    DividendDistributionRequest,
    ShareholderWriteRequest,
} from './requests';
import type {
    CalculateDistributionResponse,
    CommitDividendDeclarationResponse,
    CreateShareholderResponse,
    GetDividendDeclarationResponse,
    GetDividendDeclarationsResponse,
    GetShareholdersResponse,
    PreviewDividendDeclarationResponse,
    UpdateShareholderResponse,
} from './responses';
import { parseGelToTetri } from '@/utils/money';

type DividendsServiceOptions = {
    baseUrl: string;
};

/**
 * Decimal GEL from the form → the JSON number the write edge takes.
 *
 * Routed through `parseGelToTetri` rather than `Number(value)` so the crossing
 * happens on the integer, exactly once, as it does everywhere else in this
 * client. A blank or unparseable field is `0`, which the server refuses with
 * `NOTHING_TO_DISTRIBUTE` rather than the client inventing a message for it.
 */
const gelFieldToNumber = (value: string): number => {
    const parsed = parseGelToTetri(value);
    return gelNumberOf(parsed.ok ? parsed.tetri : 0);
};

/**
 * The shareholder write payload, shared by create and update.
 *
 * `id` travels on the create only — the update addresses the holder by path, and
 * the API's `UpdateShareholderRequest` has no id field. Everything else is sent
 * on both: an update **replaces** every field, so omitting one is not "leave it
 * alone", it is "clear it".
 *
 * `withholdingTaxBps` is sent explicitly, null included: null is "no rate
 * recorded, take the entity default", which a missing key could not express in a
 * payload the server treats as a full replacement.
 */
const shareholderPayload = (body: ShareholderWriteRequest) => ({
    name: body.name,
    identifier: body.identifier,
    type: body.type,
    residency: body.residency,
    holdingBps: body.holdingBps,
    withholdingTaxBps: body.withholdingTaxBps,
    withholdingNote: body.withholdingNote,
    citExempt: body.citExempt,
});

/**
 * The distribution payload, shared by calculate, preview and commit — one shape,
 * so a commit cannot carry a knob that changes what the preview promised.
 *
 * `date` is omitted by the calculator, which is dated by the `asOf` query
 * instead. `allocations` is always sent, empty when the split is by holding.
 */
const distributionPayload = (body: DividendDistributionRequest) => ({
    ...(body.date ? { date: body.date } : {}),
    direction: body.direction,
    amount: gelFieldToNumber(body.amount),
    allocations: (body.allocations ?? []).map((override) => ({
        shareholderId: override.shareholderId,
        gross: gelFieldToNumber(override.gross),
    })),
    ...(body.description ? { description: body.description } : {}),
    ...(body.locale ? { locale: body.locale } : {}),
});

/**
 * Real HTTP Profit & Dividends client — the register bound to `bos-api`
 * (BOS-112).
 *
 * Every method maps its response through `dividendsMapping`, which is the only
 * file that knows the wire's field names. Nothing here interprets a payload
 * beyond passing it to a mapper, and **nothing here posts to the ledger**: the
 * draft a commit returns is handed to the Journal Entry form, where a person
 * posts it under the journal's own validation and period gating.
 *
 * `locale` is sent on the declaration calls because the server's default is `ka`
 * regardless of the app's language; it localizes the draft's account names and
 * description. The register itself takes no locale — names come back as maps and
 * the display layer picks.
 */
export class DividendsService implements IDividendsService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: DividendsServiceOptions) {
        this.baseUrl = baseUrl;
    }

    private get client() {
        return getAxiosClient({ baseURL: this.baseUrl });
    }

    public async getShareholders(
        asOf: string,
    ): Promise<ApiResponse<GetShareholdersResponse>> {
        const result = await makeApiRequest<unknown>(
            this.client.get(DividendsEndpoints.shareholders({ asOf })),
        );
        return result.success
            ? { success: true, response: mapShareholderList(result.response) }
            : result;
    }

    public async createShareholder(
        body: ShareholderWriteRequest,
    ): Promise<ApiResponse<CreateShareholderResponse>> {
        const result = await makeApiRequest<unknown>(
            this.client.post(DividendsEndpoints.createShareholder(), {
                id: body.id,
                ...shareholderPayload(body),
            }),
        );
        return result.success
            ? { success: true, response: mapCreateShareholder(result.response) }
            : result;
    }

    public async updateShareholder(
        shareholderId: string,
        body: ShareholderWriteRequest,
    ): Promise<ApiResponse<UpdateShareholderResponse>> {
        const result = await makeApiRequest<unknown>(
            this.client.put(
                DividendsEndpoints.updateShareholder(shareholderId),
                shareholderPayload(body),
            ),
        );
        return result.success
            ? { success: true, response: mapUpdateShareholder(result.response) }
            : result;
    }

    /**
     * `204` carries no body, so there is nothing to map — the envelope alone is
     * the answer. A refusal (`SHAREHOLDER_HAS_DECLARATIONS`) comes back through
     * the same `ApiResponse` failure branch as every other write.
     */
    public deleteShareholder(
        shareholderId: string,
    ): Promise<ApiResponse<void>> {
        return makeApiRequest<void>(
            this.client.delete(
                DividendsEndpoints.deleteShareholder(shareholderId),
            ),
        );
    }

    public async calculateDistribution(
        body: DividendDistributionRequest,
        asOf: string,
    ): Promise<ApiResponse<CalculateDistributionResponse>> {
        const result = await makeApiRequest<unknown>(
            this.client.post(
                DividendsEndpoints.calculate(asOf),
                distributionPayload(body),
            ),
        );
        return result.success
            ? {
                  success: true,
                  response: mapCalculateDistribution(result.response),
              }
            : result;
    }

    public async getDeclarations(): Promise<
        ApiResponse<GetDividendDeclarationsResponse>
    > {
        const result = await makeApiRequest<unknown>(
            this.client.get(DividendsEndpoints.declarations()),
        );
        return result.success
            ? { success: true, response: mapDeclarationList(result.response) }
            : result;
    }

    public async getDeclaration(
        journalEntryId: string,
    ): Promise<ApiResponse<GetDividendDeclarationResponse>> {
        const result = await makeApiRequest<unknown>(
            this.client.get(DividendsEndpoints.declaration(journalEntryId)),
        );
        return result.success
            ? { success: true, response: mapDeclarationDetail(result.response) }
            : result;
    }

    public async previewDeclaration(
        body: DividendDistributionRequest,
    ): Promise<ApiResponse<PreviewDividendDeclarationResponse>> {
        const result = await makeApiRequest<unknown>(
            this.client.post(
                DividendsEndpoints.previewDeclaration(),
                distributionPayload(body),
            ),
        );
        return result.success
            ? {
                  success: true,
                  response: mapPreviewDeclaration(result.response),
              }
            : result;
    }

    public async commitDeclaration(
        body: DividendDistributionRequest,
    ): Promise<ApiResponse<CommitDividendDeclarationResponse>> {
        const result = await makeApiRequest<unknown>(
            this.client.post(
                DividendsEndpoints.commitDeclaration(),
                distributionPayload(body),
            ),
        );
        return result.success
            ? { success: true, response: mapCommitDeclaration(result.response) }
            : result;
    }
}
